'use client';

import { motion } from 'framer-motion';
import { ReactNode } from 'react';

interface PageHeaderProps {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
  gradient?: string;
}

export function PageHeader({ title, description, action, icon, gradient }: PageHeaderProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`
        flex flex-col gap-3 rounded-xl p-3 sm:p-4
        sm:flex-row sm:items-center sm:justify-between
        ${gradient 
          ? `bg-gradient-to-r ${gradient} text-white` 
          : 'bg-gradient-to-r from-white via-white to-indigo-50/50 border border-gray-100'}
      `}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {icon && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.15, type: 'spring' }}
            className={`
              flex h-10 w-10 shrink-0 items-center justify-center rounded-lg shadow-md
              ${gradient ? 'bg-white/20' : 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-indigo-500/25'}
            `}
          >
            <span className={gradient ? '' : 'text-white'}>{icon}</span>
          </motion.div>
        )}
        <div className="min-w-0 flex-1">
          <h1
            className={`text-base font-bold sm:text-lg ${gradient ? 'text-white' : 'text-gray-900'}`}
          >
            {title}
          </h1>
          {description && (
            <p
              className={`mt-0.5 text-xs leading-relaxed sm:text-sm ${gradient ? 'text-white/80' : 'text-gray-500'}`}
            >
              {description}
            </p>
          )}
        </div>
      </div>
      {action && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15 }}
          className="w-full shrink-0 sm:w-auto"
        >
          {action}
        </motion.div>
      )}
    </motion.div>
  );
}
